import { Trash2 } from "lucide-react";

import { getOptimizePackagePlan } from "react-lib/apps/HISV3/HCU/sequence/PackagePurchase";

import {
  amountText,
  appointmentText,
  deleteBlockText,
  isComparable,
  isPaid,
  priceText,
  quantityText,
  selectedDateText,
  totalOf,
  usageText,
  type PackageOrder,
} from "./packageModelV4";

// Types
type PackageSelectedTabV4Props = {
  // data
  orders: PackageOrder[];
  checked: number[];
  loading: boolean;
  ordering: boolean;
  compareOpen: boolean;
  optimizing: boolean;
  /** มีผลคำนวณแพ็กเกจที่คุ้มกว่าแล้ว */
  optimized: boolean;
  plan: ReturnType<typeof getOptimizePackagePlan> | null;
  // callback
  onCheck: (order: PackageOrder, value: boolean) => void;
  onChangeQty: (order: PackageOrder, step: number) => void;
  onRemove: (order: PackageOrder) => void;
  onGoSale: () => void;
  onGoAppointments?: () => void;
  onOrder: () => void;
  onCompare: () => void;
};

const PackageSelectedTabV4 = (props: PackageSelectedTabV4Props) => {
  const { orders, checked, plan } = props;

  const checkedOrders = orders.filter((order) => checked.includes(order.id));
  const compareOrders = checkedOrders.filter(isComparable);

  // Handler
  const handleGoAppointments = () => {
    props.onGoAppointments?.();
  };

  // Render
  const renderEmpty = () => (
    <div className="hv4-pkg-sel-empty">
      ยังไม่ได้เลือกแพ็กเกจ · ไปที่แท็บ "แพ็กเกจที่เปิดขาย" แล้วกดปุ่มรถเข็นที่แพ็กเกจที่ต้องการ
      <div>
        <button type="button" className="hv4-pkg-bt hv4-pkg-bt-go" onClick={props.onGoSale}>
          ไปเลือกแพ็กเกจ
        </button>
      </div>
    </div>
  );

  const renderRow = (order: PackageOrder) => {
    const paid = isPaid(order);
    const blocked = deleteBlockText(order);
    const isChecked = checked.includes(order.id);

    return (
      <div
        key={order.id}
        className={`hv4-pkg-row${isChecked ? " hv4-pkg-row--on" : ""}`}
        role="row"
      >
        <span role="cell">
          <input
            type="checkbox"
            className="hv4-pkg-check"
            aria-label={`เลือก ${order.product_name}`}
            checked={isChecked}
            onChange={(e) => props.onCheck(order, e.target.checked)}
          />
        </span>
        <span role="cell">{selectedDateText(order)}</span>
        <span role="cell">{order.product_code}</span>
        <b role="cell">{order.product_name}</b>
        <span role="cell" className="hv4-pkg-r">
          {priceText(order.price)}
        </span>
        <span role="cell" className="hv4-pkg-qty">
          <button type="button" aria-label="ลดจำนวน" onClick={() => props.onChangeQty(order, -1)}>
            −
          </button>
          <span>{quantityText(order.quantity)}</span>
          <button type="button" aria-label="เพิ่มจำนวน" onClick={() => props.onChangeQty(order, 1)}>
            +
          </button>
        </span>
        <b role="cell" className={paid ? "hv4-pkg-paid" : "hv4-pkg-unpaid"}>
          {paid ? "ชำระแล้ว" : "รอชำระเงิน"}
        </b>
        <span role="cell">{appointmentText(order)}</span>
        <span role="cell" className="hv4-pkg-usage">
          {usageText(order)}
        </span>
        <span role="cell" className="hv4-pkg-c">
          <button
            type="button"
            className="hv4-pkg-icon hv4-pkg-del"
            title={blocked ?? `เอาออก ${order.product_name}`}
            aria-label={blocked ?? `เอาออก ${order.product_name}`}
            disabled={!!blocked}
            onClick={() => props.onRemove(order)}
          >
            <Trash2 size={22} />
          </button>
        </span>
      </div>
    );
  };

  const renderTable = () => (
    <div className="hv4-pkg-scroll">
      <div className="hv4-pkg-table hv4-pkg-sel" role="table" aria-label="แพ็กเกจที่เลือก">
        <div className="hv4-pkg-row hv4-pkg-head" role="row">
          <span role="columnheader" />
          <span role="columnheader">วันที่เลือก</span>
          <span role="columnheader">รหัส</span>
          <span role="columnheader">ชื่อแพ็กเกจ</span>
          <span role="columnheader" className="hv4-pkg-r">
            ราคา
          </span>
          <span role="columnheader" className="hv4-pkg-c">
            จำนวน
          </span>
          <span role="columnheader">การชำระเงิน</span>
          <span role="columnheader">วันที่นัดหมาย</span>
          <span role="columnheader">การใช้งาน</span>
          <span role="columnheader" />
        </div>
        {orders.map(renderRow)}
      </div>
    </div>
  );

  const renderActions = () => (
    <div className="hv4-pkg-actions">
      <button type="button" className="hv4-pkg-bt hv4-pkg-bt-appt" onClick={handleGoAppointments}>
        ทำนัดหมาย
      </button>
      <button
        type="button"
        className="hv4-pkg-bt hv4-pkg-bt-order"
        disabled={!checked.length || props.ordering}
        onClick={props.onOrder}
      >
        สั่ง Order
      </button>
      <button type="button" className="hv4-pkg-bt hv4-pkg-bt-compare" onClick={props.onCompare}>
        คำนวณแพ็กเกจที่คุ้มกว่า
      </button>
      <div className="hv4-pkg-total">
        <b>ราคารวม</b> <span className="hv4-pkg-total-n">({checkedOrders.length} รายการ)</span>{" "}
        <b className="hv4-pkg-total-amt">{amountText(totalOf(checkedOrders))}</b> บาท
      </div>
    </div>
  );

  const renderCompare = () =>
    props.compareOpen &&
    checked.length > 0 && (
      <div className="hv4-pkg-compare" role="status">
        <b>เปรียบเทียบความคุ้มของรายการที่เลือก</b>
        {checkedOrders
          .filter((order) => !isComparable(order))
          .map((order) => (
            <span key={order.id}>
              {order.product_name} · ราคาแพ็กเกจ ฿{priceText(order.price)} ·
              ไม่มีรายการที่เทียบราคาได้
            </span>
          ))}
        {compareOrders.length > 0 &&
          (props.optimizing ? (
            <span>กำลังคำนวณ</span>
          ) : plan && plan.savings > 0 ? (
            <>
              {plan.packages.map((row) => (
                <span key={row.package.id}>
                  {row.package.name} × {row.quantity} · ราคาแพ็กเกจ ฿{priceText(row.total_price)}
                </span>
              ))}
              <span>
                รายการที่เลือก ฿{priceText(plan.selectedTotal)} · ซื้อตามแพ็กเกจที่แนะนำ ฿
                {priceText(plan.total)} · ประหยัด ≈ ฿{priceText(plan.savings)}
              </span>
            </>
          ) : (
            props.optimized && <span>ไม่มีแพ็กเกจที่คุ้มกว่ารายการที่เลือก</span>
          ))}
      </div>
    );

  return (
    <section className="hv4-card hv4-pkg-panel">
      {orders.length === 0 && props.loading ? (
        <div className="hv4-pkg-sel-empty">กำลังโหลด</div>
      ) : orders.length === 0 ? (
        renderEmpty()
      ) : (
        <>
          {renderTable()}
          {renderActions()}
          {renderCompare()}
        </>
      )}
    </section>
  );
};

PackageSelectedTabV4.displayName = "PackageSelectedTabV4";

export default PackageSelectedTabV4;
